/**
 * Which interview link an invite goes out on — shown ONLY when a role+stage has more than one.
 *
 * A stage can legitimately carry several live links (one per interviewer was how TA ran Lever
 * Easy Book). The proxy already pre-selects a derived default — the recruiter's own link, else the
 * newest — so this is an override for the exception, never a step: with one link it renders
 * nothing, and with several the right one is already chosen. The choice is per send, not a saved
 * setting.
 *
 * Shared by the Move modal and the bulk first-interview invite, so the two cannot describe the
 * same links differently.
 */
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@finn-auto/design-system/atoms/select';
import type { InterviewLinkOption } from '../../services/inviteDraftApi';

/** "Anna, Ben" — first names, falling back to the address. Who interviews is what tells links apart. */
function interviewerLabel(option: InterviewLinkOption): string {
  const names = option.interviewers.map(
    (i) => (i.name || '').trim().split(/\s+/)[0] || i.email.split('@')[0]
  );
  return names.length ? names.join(', ') : 'no interviewer';
}

function optionLabel(option: InterviewLinkOption, ownEmail?: string): string {
  const own =
    !!ownEmail &&
    option.interviewers.some((i) => i.email.toLowerCase() === ownEmail.trim().toLowerCase());
  return `${interviewerLabel(option)}${option.name ? ` — ${option.name}` : ''}${own ? ' (yours)' : ''}`;
}

export function LinkPicker({
  options,
  value,
  onChange,
  ownEmail,
  disabled = false,
  label = 'Interview link',
}: {
  options: InterviewLinkOption[] | undefined;
  /** The link the current draft was built on (the server's pick, or the recruiter's override). */
  value: string | null | undefined;
  onChange: (linkId: string) => void;
  /** The board owner — marks their own link "(yours)" so the default explains itself. */
  ownEmail?: string;
  disabled?: boolean;
  label?: string;
}) {
  if (!options || options.length < 2 || !value) return null;
  return (
    <div className="min-w-[14rem] flex-1" data-testid="link-picker">
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        {/* w-full: the DS trigger defaults to w-max, which cut long names off */}
        <SelectTrigger label={`${label} (${options.length} for this stage)`} className="w-full">
          <SelectValue placeholder="Choose a link" />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.id} value={o.id}>
              {optionLabel(o, ownEmail)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
